import { useCallback, useEffect, useState } from 'react';
import { useGeneralProjectionSettings, useUpdateProjectionCashFlowGeneralSettings } from '../api';
import type { MonteCarloGeneralSettingsDto } from '../api/generated/client';

/**
 * Keeps general projection settings in sync with the server query and applies optimistic updates
 * with rollback on mutation error (recommended pattern when the UI must stay responsive).
 */
export function useGeneralSimulationSettings(): {
    settings: MonteCarloGeneralSettingsDto | undefined;
    patchSettings: (partial: Partial<MonteCarloGeneralSettingsDto>) => void;
} {
    const { data } = useGeneralProjectionSettings();
    const updateGeneralSettings = useUpdateProjectionCashFlowGeneralSettings();
    const [settings, setSettings] = useState<MonteCarloGeneralSettingsDto | undefined>(undefined);

    useEffect(() => {
        if (data !== undefined) setSettings(data);
    }, [data]);

    const patchSettings = useCallback(
        (partial: Partial<MonteCarloGeneralSettingsDto>) => {
            setSettings((prev) => {
                if (!prev) return prev;

                const next = { ...prev, ...partial };
                updateGeneralSettings.mutate(next, { onError: () => setSettings(prev) });

                return next;
            });
        },
        [updateGeneralSettings],
    );

    return { settings, patchSettings };
}
